import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  LabelList,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Box, Typography } from "@mui/material";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DataItem {
  name: string;
  shortName: string;
  value: number;
  color: string;
  activeColor: string;
}

interface CustomLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
  index?: number;
  activeIndex: number | null;
  data: DataItem[];
}

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  index?: number;
  activeIndex: number | null;
}

interface LegendDotProps {
  color: string;
  activeColor: string;
  filled: boolean;
  active: boolean;
  anyActive: boolean;
}

interface BudgetControlGraphViewProps {
  data: DataItem[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
}

const DIMMED_OPACITY = 0.4;

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Top-of-bar value label — only visible when the bar is selected */
function CustomLabel({
  x = 0,
  y = 0,
  width = 0,
  value = 0,
  index = 0,
  activeIndex,
  data,
}: CustomLabelProps) {
  if (activeIndex !== index) return null;
  const fillColor = data[index]?.activeColor ?? data[index]?.color;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      textAnchor="middle"
      fill={fillColor}
      fontWeight={700}
      fontSize={13}
    >
      {Math.round(value)}%
    </text>
  );
}

/** Multi-line X-axis tick with active/dimmed state */
function CustomXAxisTick({
  x = 0,
  y = 0,
  payload,
  index = 0,
  activeIndex,
}: CustomXAxisTickProps) {
  const lines = (payload?.value ?? "").split("\n");
  const isActive = activeIndex === null || activeIndex === index;
  const baseColor0 = isActive ? "colors.GrayLightMode[700].$value" : "#9CA3AF";
  const baseColor1 = isActive ? "colors.GrayLightMode[700].$value" : "#9CA3AF";
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={0}
          y={0}
          dy={16 + i * 14}
          textAnchor="middle"
          fill={i === 0 ? baseColor0 : baseColor1}
          fontSize={10}
          fontWeight={400}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/** Circular legend indicator — filled for first item, outlined for the rest */
function LegendDot({ color, activeColor, filled, active }: LegendDotProps) {
  // When something is selected: selected bar → activeColor, others → base color (no dim)
  const dotColor = active ? activeColor : color;

  if (filled) {
    return (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: dotColor,
          display: "inline-block",
          transition: "all 0.2s",
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        display: "inline-block",
        background: dotColor,
        transition: "all 0.2s",
      }}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BudgetControlGraphView({
  data,
  activeIndex,
  onSelect,
}: BudgetControlGraphViewProps) {
  const handleBarClick = (_: unknown, index: number): void => {
    onSelect(index);
  };

  const handleLegendClick = (index: number): void => {
    onSelect(index);
  };

  const anyActive = activeIndex !== null;

  // Dynamically scale the Y-axis when a brand exceeds 125% (mirrors desktop)
  const maxValue = useMemo(() => {
    if (data.length === 0) return 125;
    return Math.max(125, Math.max(...data.map((d) => d.value)));
  }, [data]);

  const yAxisTicks = useMemo(() => {
    const base = [0, 25, 50, 75, 100, 125];
    if (maxValue <= 125) return base;
    // beyond 125, keep adding ticks in 25% increments
    const extra = [...base];
    let next = 150;
    while (next <= maxValue + 10) {
      extra.push(next);
      next += 25;
    }
    return extra;
  }, [maxValue]);

  // No brands to plot → show a centered "No Data" message inside the graph box
  if (data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 240,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          color={colors.GrayLightMode[700].$value}
          sx={{
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            fontWeight: typography.Font_weight.Medium.$value,
          }}
        >
          No Data
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ maxWidth: 560, width: "100%", userSelect: "none" }}>
      {/* ── Legend ── */}
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {data.map((item, i) => {
          const isActive = activeIndex === i;
          // No opacity dimming — unselected items stay full opacity but use base color
          return (
            <div
              key={`${item.shortName}-${i}`}
              onClick={() => handleLegendClick(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                padding: "4px 6px",
                borderRadius: spacing.Radius.xs.$value,
                background: isActive ? `${item.activeColor}` : "transparent",
                transition: "all 0.2s",
              }}
            >
              <LegendDot
                color={item.color}
                activeColor={colors.Base.White.$value}
                filled={i === 0}
                active={isActive}
                anyActive={anyActive}
              />
              <span
                style={{
                  fontSize: 10,
                  color: isActive
                    ? colors.Base.White.$value
                    : colors.GrayLightMode[700].$value,
                  fontWeight: 400,
                  transition: "all 0.2s",
                }}
              >
                {item.shortName}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Chart ── */}
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 0, left: -20, bottom: 10 }}
          barCategoryGap="20%"
        >
          <CartesianGrid vertical={false} stroke="#F3F4F6" />

          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={(props) => (
              <CustomXAxisTick
                {...props}
                index={props.index as number}
                activeIndex={activeIndex}
              />
            )}
          />

          <YAxis
            tickFormatter={(v: number) => `${v}%`}
            axisLine={false}
            tickLine={false}
            tick={{ fill: colors.GrayLightMode[700].$value, fontSize: 10 }}
            domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
            ticks={yAxisTicks}
          />

          <ReferenceLine
            y={100}
            stroke="#9CA3AF"
            strokeDasharray="5 4"
            strokeWidth={1}
          />

          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            maxBarSize={72}
            onClick={handleBarClick}
            cursor="pointer"
          >
            {data.map((entry, index) => {
              const isActive = activeIndex === index;
              // Selected → activeColor at full opacity
              // Unselected (when something else is selected) → base color dimmed
              // Nothing selected → base color full opacity
              return (
                <Cell
                  key={index}
                  fill={isActive ? entry.activeColor : entry.color}
                  opacity={anyActive && !isActive ? DIMMED_OPACITY : 1}
                  style={{ transition: "opacity 0.2s, fill 0.2s" }}
                />
              );
            })}
            <LabelList
              dataKey="value"
              content={(props) => (
                <CustomLabel
                  {...(props as CustomLabelProps)}
                  activeIndex={activeIndex}
                  data={data}
                />
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
